/**
 * §21.1 Finanzas — registro de Gastos (CRUD). DATO DEL DUEÑO: solo se monta para
 * roles con financials:view (gateado en Dashboard + API 403). Categoría free-text
 * con sugerencias (datalist). Reusa el cliente api(). Soft-delete en el backend.
 */
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { api } from '../lib/api';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHOD_LABEL: Record<string, string> = { CASH: 'Efectivo', CARD: 'Tarjeta', TRANSFER: 'Transferencia' };
const COMMON_CATEGORIES = ['Renta', 'Nómina', 'Insumos', 'Servicios', 'Mantenimiento', 'Marketing', 'Impuestos', 'Comisiones', 'Transporte', 'Otros'];

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';
const btnDanger = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

interface Expense {
  id: string; amount: number; category: string; expenseDate: string;
  paymentMethod: string; vendor: string | null; note: string | null; createdAt: string;
}

function L({ label, children }: { label: string; children: any }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

// #249 — panel limitado a `max-h-[90vh]` con el cuerpo scrolleable y el header
// fijo, para que en tablets el formulario no desborde la pantalla dejando los
// botones fuera de alcance.
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: any }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

export function ExpensesView() {
  const [rows, setRows] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api('/finance/expenses');
      setRows(Array.isArray(r) ? (r as Expense[]) : []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (e: Expense) => {
    if (!window.confirm(`¿Eliminar el gasto de ${mxn(e.amount)} (${e.category})?`)) return;
    await api(`/finance/expenses/${e.id}`, { method: 'DELETE' });
    load();
  };

  const total = rows.reduce((a, b) => a + Number(b.amount ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Gastos</h2>
          <p className="text-sm text-slate-500">Registro de egresos del negocio — alimenta el reporte de Ingresos vs gastos.</p>
        </div>
        <button className={btnPrimary} onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 inline h-4 w-4" /> Nuevo gasto
        </button>
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Categoría</th>
                <th className="px-4 py-2">Proveedor / nota</th>
                <th className="px-4 py-2">Método</th>
                <th className="px-4 py-2 text-right">Monto</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Aún no registras gastos. Crea el primero.</td></tr>
              )}
              {rows.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-2 whitespace-nowrap text-slate-600">{e.expenseDate}</td>
                  <td className="px-4 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{e.category}</span></td>
                  <td className="px-4 py-2 text-slate-600">{e.vendor || e.note || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-2 text-slate-600">{METHOD_LABEL[e.paymentMethod] ?? e.paymentMethod}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium text-slate-800">{mxn(e.amount)}</td>
                  <td className="px-4 py-2 space-x-2 text-right">
                    <button className={btnGhost} onClick={() => setEditing(e)}>Editar</button>
                    <button className={btnDanger} onClick={() => remove(e)}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50/60">
                  <td className="px-4 py-2 text-[11px] uppercase tracking-wide text-slate-500" colSpan={4}>Total ({rows.length})</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900">{mxn(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {createOpen && <ExpenseModal onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
      {editing && <ExpenseModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ExpenseModal({ initial, onClose, onSaved }: { initial?: Expense; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!initial;
  const [f, setF] = useState({
    amount: initial ? String(initial.amount) : '',
    category: initial?.category ?? '',
    expenseDate: initial?.expenseDate ?? new Date().toISOString().slice(0, 10),
    paymentMethod: initial?.paymentMethod ?? 'CASH',
    vendor: initial?.vendor ?? '',
    note: initial?.note ?? '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: any) => {
    e.preventDefault(); setErr(null);
    if (!f.amount || Number(f.amount) <= 0) { setErr('El monto debe ser mayor a 0.'); return; }
    if (!f.category.trim()) { setErr('La categoría es obligatoria.'); return; }
    setBusy(true);
    const body = {
      amount: Number(f.amount),
      category: f.category.trim(),
      expenseDate: f.expenseDate,
      paymentMethod: f.paymentMethod,
      vendor: f.vendor.trim() || undefined,
      note: f.note.trim() || undefined,
    };
    try {
      if (isEdit) await api(`/finance/expenses/${initial!.id}`, { method: 'PATCH', body });
      else await api('/finance/expenses', { method: 'POST', body });
      onSaved();
    } catch (e2: any) {
      setErr(e2?.body?.message || e2?.message || 'No se pudo guardar.');
    } finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? 'Editar gasto' : 'Nuevo gasto'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <L label="Monto *"><input className="input" type="number" min="0" step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0.00" /></L>
          <L label="Fecha *"><input className="input" type="date" required value={f.expenseDate} onChange={(e) => setF({ ...f, expenseDate: e.target.value })} /></L>
        </div>
        <L label="Categoría *">
          <input className="input" list="expense-cat-list" maxLength={50} required value={f.category}
            placeholder="Renta, Nómina, Insumos…" onChange={(e) => setF({ ...f, category: e.target.value })} />
          <datalist id="expense-cat-list">{COMMON_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
          <span className="mt-1 block text-[10px] text-slate-400">Elige una sugerida o escribe la tuya.</span>
        </L>
        <div className="grid grid-cols-2 gap-3">
          <L label="Método de pago">
            <select className="input" value={f.paymentMethod} onChange={(e) => setF({ ...f, paymentMethod: e.target.value })}>
              <option value="CASH">Efectivo</option>
              <option value="CARD">Tarjeta</option>
              <option value="TRANSFER">Transferencia</option>
            </select>
          </L>
          <L label="Proveedor (opcional)"><input className="input" maxLength={200} value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} /></L>
        </div>
        <L label="Nota (opcional)"><textarea className="input" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></L>
        {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={btnGhost} onClick={onClose}>Cancelar</button>
          <button className={btnPrimary} disabled={busy}>{busy ? 'Guardando…' : (isEdit ? 'Guardar' : 'Crear')}</button>
        </div>
      </form>
    </Modal>
  );
}
